import * as React from "react";
import {LatLong, MarkerSpot, Spot, SpotType, Zone} from "../../components/types";

export const PANEL_SPOTS = "spots";
export const PANEL_ZONES = "zones";
export const PANEL_MAINTENANCE = "maintenance";

export const MARKER_OFFSET_STEP = 0.000012;

export const hasDroppedFiles = (dataTransfer?: DataTransfer | null) => {
    if (!dataTransfer) {
        return false;
    }
    if (dataTransfer.files && dataTransfer.files.length > 0) {
        return true;
    }
    return Array.from(dataTransfer.types || []).includes("Files");
};

export const getDroppedFiles = (dataTransfer?: DataTransfer | null) => {
    if (!dataTransfer?.files?.length) {
        return [];
    }
    return Array.from(dataTransfer.files);
};

export const getDropTargetExitHandler = (setActive: (value: boolean) => void) =>
    (event: React.DragEvent<HTMLDivElement>) => {
        const nextTarget = event.relatedTarget as Node | null;
        if (!nextTarget || !event.currentTarget.contains(nextTarget)) {
            setActive(false);
        }
    };

export const getDroppedMarkerPosition = (existingSpots: Spot[], mapCenter?: LatLong, sequenceIndex = 0): LatLong => {
    const markerSpots = existingSpots.filter((spot): spot is MarkerSpot => {
        return spot.type === SpotType.Marker
            && !!(spot as MarkerSpot).position
            && typeof (spot as MarkerSpot).position?.lat === "number"
            && typeof (spot as MarkerSpot).position?.lng === "number";
    });
    const center = markerSpots.length > 0
        ? markerSpots.reduce((acc, marker) => ({
            lat: acc.lat + ((marker.position?.lat || 0) / markerSpots.length),
            lng: acc.lng + ((marker.position?.lng || 0) / markerSpots.length)
        }), {lat: 0, lng: 0})
        : mapCenter || {lat: 0, lng: 0};
    const candidateIndex = markerSpots.length + sequenceIndex;
    if (candidateIndex === 0) {
        return center;
    }
    const ring = Math.floor((candidateIndex - 1) / 4) + 1;
    const direction = (candidateIndex - 1) % 4;
    const distance = MARKER_OFFSET_STEP * ring;
    switch (direction) {
        case 0:
            return {lat: center.lat + distance, lng: center.lng};
        case 1:
            return {lat: center.lat, lng: center.lng + distance};
        case 2:
            return {lat: center.lat - distance, lng: center.lng};
        default:
            return {lat: center.lat, lng: center.lng - distance};
    }
};

export const stripFileExtension = (fileName: string) => {
    const trimmed = (fileName || "").trim();
    if (!trimmed) {
        return trimmed;
    }
    const dotIndex = trimmed.lastIndexOf(".");
    if (dotIndex <= 0 || dotIndex === trimmed.length - 1) {
        return trimmed;
    }
    return trimmed.substring(0, dotIndex);
};

export const getNextSpotName = (spots: Spot[]) => {
    const uniqueSpots = Array.from(new Map(spots.map((spot) => [spot.id, spot])).values());
    const highestNumber = uniqueSpots.reduce((max, spot) => {
        const match = /^(?:\d+\s*-\s*)?Spot\s+(\d+)$/.exec(spot.name || "");
        if (!match) {
            return max;
        }
        const num = parseInt(match[1], 10);
        return Number.isFinite(num) && num > max ? num : max;
    }, 0);
    const next = highestNumber + 1;
    return `${next} - Spot ${next}`;
};

export const getNextAmbientZoneName = (zones: Zone[]) => {
    const highestNumber = zones.reduce((max, zone) => {
        const match = /^Ambient zone\s+(\d+)$/i.exec((zone.label || zone.name || "").trim());
        if (!match) {
            return max;
        }
        const num = parseInt(match[1], 10);
        return Number.isFinite(num) && num > max ? num : max;
    }, 0);
    return `Ambient zone ${highestNumber + 1}`;
};
